import React from "react";
import { ArrowRight, Sparkles, Star } from "lucide-react";
import { Button } from "./ui/button";
import StoreBadges from "./StoreBadges";
import { SHOWCASE_IMAGES, HERO_STATS } from "../mock";

const Hero = ({ onTrackDownload }) => {
  return (
    <section id="top" className="relative min-h-[100vh] w-full overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={SHOWCASE_IMAGES.heroBg}
          alt="Desert road trip"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/70 via-emerald-950/40 to-[#f6f1e7]" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 pt-40 md:pt-48 pb-24 md:pb-28">
        <div className="max-w-3xl fade-in-up">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-md border border-white/20 px-4 py-1.5 mb-6">
            <Sparkles className="w-4 h-4 text-amber-300" />
            <span className="text-xs md:text-sm uppercase tracking-[0.18em] text-white/90 font-medium">
              Group travel without the spreadsheet
            </span>
          </div>

          <h1 className="font-display font-semibold text-white text-5xl sm:text-6xl md:text-7xl lg:text-[86px] leading-[0.98]">
            One place for the <span className="italic text-amber-200">whole trip.</span>
          </h1>

          <p className="mt-6 text-white/85 text-lg md:text-xl max-w-xl leading-relaxed">
            Plan the itinerary, split expenses, map every stop, chat with members, and keep the memories — all in one beautifully designed app.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button asChild size="lg" className="rounded-full bg-emerald-800 hover:bg-emerald-700 text-white px-7 h-12 text-base">
              <a href="#get-app" className="inline-flex items-center gap-2">
                Start planning <ArrowRight className="w-4 h-4" />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-7 bg-white/10 hover:bg-white/20 backdrop-blur-md border-white/30 text-white hover:text-white">
              <a href="#features">See how it works</a>
            </Button>
          </div>

          <div className="mt-10">
            <div className="text-white/75 text-xs uppercase tracking-[0.2em] mb-3">Download for free</div>
            <StoreBadges size="md" theme="dark" onClickTrack={onTrackDownload} />
          </div>

          <div className="mt-10 flex items-center gap-4">
            <div className="flex -space-x-2">
              {["1544005313-94ddf0286df2","1500648767791-00dcc994a43e","1580489944761-15a19d654956","1531123897727-8f129e1688ce"].map((id, i) => (
                <img key={i} src={`https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=100&q=80`} alt="user" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
              ))}
            </div>
            <div className="text-white/85 text-sm">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-amber-300 text-amber-300" />
                ))}
                <span className="ml-1 font-semibold">4.9</span>
              </div>
              <div className="text-xs text-white/70">Loved by 20k+ travellers worldwide</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom stats strip */}
      <div className="relative max-w-7xl mx-auto px-6 pb-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {HERO_STATS.map((s, i) => (
            <div key={i} className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-5 py-4">
              <div className="font-display text-white text-xl md:text-2xl font-semibold">{s.title}</div>
              <div className="text-white/75 text-xs md:text-sm mt-0.5">{s.subtitle}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
